import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../hooks/useCart';
import { medirTinta } from '../../utils/medirTinta';

/**
 * Botón "Agregar" en forma de píldora con relieve: una caja verde oscura que
 * hace de borde y de zócalo (más grueso abajo) y, encima, la pastilla blanca.
 * No es una imagen porque tiene que convertirse en stepper "− qty +" cuando el
 * producto ya está en el carrito, y porque así escala con el ancho de la card.
 *
 * Hermano de AddToCartButton (el "+" circular). Comparten comportamiento pero
 * no forma: aquí la acción es ancha y etiquetada, allá es un icono sobre la
 * foto. Si aparece una tercera variante, extraer el estado del carrito a un
 * hook común (useProductQuantity) antes de duplicar otra vez.
 *
 * Props:
 *   - product: objeto del producto (debe tener _id o id)
 *   - businessId: opcional, se mergea al producto antes de addItem
 *   - label: texto cuando hay 0 unidades (default "Agregar")
 *   - image: URL opcional. Si viene, con 0 unidades se pinta esa imagen en vez
 *     de la píldora dibujada. El stepper (qty > 0) SIEMPRE es el dibujado: una
 *     imagen no puede mostrar la cantidad ni reaccionar al −/+. Con imagen,
 *     el stepper toma el tamaño exacto del dibujo del botón.
 */
export default function AddProductButton({ product, businessId, label = 'Agregar', image = null }) {
  const { quickAdd, getItem, incrementQuantity, decrementQuantity } = useCart();
  const productId = product._id || product.id;
  const qty = getItem(productId)?.quantity || 0;

  // Dónde está el dibujo dentro de la imagen del botón (sin su margen
  // transparente), para que el stepper mida exactamente lo mismo.
  const [tinta, setTinta] = useState(null);
  useEffect(() => {
    let vivo = true;
    medirTinta(image).then((t) => { if (vivo) setTinta(t); });
    return () => { vivo = false; };
  }, [image]);

  const agregar = (e) => {
    e.stopPropagation();
    // Con extras abre la hoja para elegirlos; sin extras agrega directo.
    quickAdd(businessId ? { ...product, businessId } : product);
  };

  const quitar = (e) => {
    e.stopPropagation();
    // decrementQuantity(id) sin más baja de 1 en 1; en el último hay que pasar
    // el mínimo 0 para que salga del carrito en vez de quedarse en 1.
    if (qty === 1) decrementQuantity(productId, 1, 0);
    else decrementQuantity(productId);
  };

  // Con imagen, el stepper llena la caja del dibujo del botón (ver abajo):
  // mismo tamaño que la imagen, y −/+ de un tercio del ancho cada uno para
  // que se atinen con el dedo. Sin imagen, la píldora de siempre (h-7).
  const lleno = !!image;
  const anchoBoton = lleno ? 'w-1/3' : 'w-6';

  const pildora = (
    <div
      className={`rounded-[0.7rem] bg-addbtn-edge pt-[2px] px-[2px] pb-[4px] select-none ${lleno ? 'h-full flex flex-col' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`flex items-center justify-center rounded-[0.6rem] bg-white px-1 ${lleno ? 'flex-1' : 'h-7'}`}>
        <AnimatePresence mode="popLayout" initial={false}>
          {qty > 0 && (
            <motion.button
              key="menos"
              type="button"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
              onClick={quitar}
              aria-label="Quitar uno"
              className={`flex items-center justify-center ${anchoBoton} h-full flex-shrink-0 active:scale-90 transition-transform`}
            >
              <svg width={lleno ? 14 : 11} height="3" viewBox="0 0 12 3" fill="none" aria-hidden="true">
                <path d="M1.5 1.5H10.5" stroke="var(--color-addbtn-text)" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>

        {qty > 0 ? (
          <>
            <span className={`flex-1 text-center font-extrabold text-addbtn-text tabular-nums ${lleno ? 'text-[15px]' : 'text-[13px]'}`}>
              {qty}
            </span>
            <button
              type="button"
              onClick={agregar}
              aria-label="Agregar uno"
              className={`flex items-center justify-center ${anchoBoton} h-full flex-shrink-0 active:scale-90 transition-transform`}
            >
              <svg width={lleno ? 14 : 11} height={lleno ? 14 : 11} viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 1.5V10.5M1.5 6H10.5" stroke="var(--color-addbtn-text)" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={agregar}
            className="w-full h-full text-[12px] font-extrabold uppercase tracking-wide text-addbtn-text active:scale-[0.97] transition-transform"
          >
            {label}
          </button>
        )}
      </div>
    </div>
  );

  if (!image) return pildora;

  // Con imagen: la imagen ES el botón. Con unidades en el carrito se queda
  // como espaciador invisible y el stepper va encima, sobre la caja exacta del
  // dibujo (medida sin el margen transparente del PNG): así mide lo mismo que
  // el botón que se veía, y los botones de una fila no se desnivelan cuando
  // uno ya está en el carrito y otro no. Mientras no hay medida, la caja
  // completa de la imagen.
  const caja = tinta
    ? { left: `${tinta.x * 100}%`, top: `${tinta.y * 100}%`, width: `${tinta.ancho * 100}%`, height: `${tinta.alto * 100}%` }
    : { inset: 0 };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={agregar}
        disabled={qty > 0}
        aria-hidden={qty > 0}
        tabIndex={qty > 0 ? -1 : 0}
        className={`block w-full active:scale-[0.97] transition-transform ${qty > 0 ? 'invisible' : ''}`}
        aria-label={label}
      >
        <img src={image} alt={label} className="w-full h-auto" loading="lazy" />
      </button>
      {qty > 0 && (
        <div className="absolute" style={caja}>{pildora}</div>
      )}
    </div>
  );
}
